import React, { useState, useEffect } from "react";
import MainCard from '@/components/MainCard';
import { Button, Space } from "antd";
import DataTable from '@/components/DataTable';
import { PlayCircle } from "lucide-react";
import {
  bp_get_batch_process_active,
  bp_get_batch_process_schedule,
} from '@/utils/api';
import { useNavigate } from "react-router-dom";
import { defaultColors } from '@/themes/antd-theme';
import { useIngredientsAlias } from '@/hooks/useAppSetting';
import { opBehaviour } from '@/pages/inspection/api';
import { LayoutGrid } from 'lucide-react';
import OperatorAppLink from '@/utils/operatorApp';

const Batch = () => {
  const ingredientsAlias = useIngredientsAlias();
  const [user_access_data, set_user_access_data] = useState([]);

  useEffect(() => {
    try {
      const data = localStorage.getItem("user_access_data");
      if (data && data !== "undefined" && data !== "null") {
        set_user_access_data(JSON.parse(data));
      }
    } catch (error) {
      console.error("Error parsing user_access_data from localStorage:", error);
      localStorage.removeItem("user_access_data");
    }
  }, []);

  const navigate = useNavigate();
  const [operation_data, set_operation_data] = useState([]);

  const get_data = async () => {
    try {
      await bp_get_batch_process_active(success_bp_get_batch_process_active);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const success_bp_get_batch_process_active = (res) => {
    set_operation_data(res?.data?.data);
    console.log("operation_data", res?.data?.data);
  };

  useEffect(() => {
    get_data();
  }, []);

  const get_add_routes = () => {
    return user_access_data?.find((item) => item.module_name === "Batches")
      ?.create_access
      ? [
          {
            title: ingredientsAlias,
            dataIndex: "",
            key: "ingredients",
            align: "center",
            render: (_, record) => {
              return (
                record?.ingredients?.length > 0 &&
                record?.ingredients?.some(
                  (item) => item.ingredient_qty === ""
                ) && (
                  <Button
                    type="text"
                    icon={<PlayCircle size={20} style={{ color: defaultColors.primary }} />}
                    onClick={() => {
                      navigate("/ingredients", {
                        state: {
                          operation_data: record,
                        },
                      });
                    }}
                  />
                )
              );
            },
          },
          {
            title: "Parameters",
            dataIndex: "",
            key: "parameters",
            align: "center",
            render: (_, record) => {
              // UNIT_COUNT operations (visual inspection, stopper measurement, AQL…) are executed
              // per tub at stations — open the Lot Board instead of the reading form.
              if (opBehaviour(record) === "UNIT_COUNT") {
                return (
                  <Space size={0}>
                    <Button
                      type="text"
                      title="Open lot board"
                      icon={<LayoutGrid size={20} style={{ color: defaultColors.primary }} />}
                      onClick={() => navigate(`/inspection/board/${encodeURIComponent(record?.batch_number)}?op=${record?.operation_id ?? ""}`)}
                    />
                    <OperatorAppLink type="text" size="middle" iconOnly batch={record?.batch_number} operation={record?.operation_id} />
                  </Space>
                );
              }
              return (
                record?.paramaters?.length > 0 && (
                  <Button
                    type="text"
                    icon={<PlayCircle size={20} style={{ color: defaultColors.primary }} />}
                    onClick={() => {
                      navigate("/parameters", {
                        state: {
                          operation_data: record,
                        },
                      });
                    }}
                  />
                )
              );
            },
          },
          {
            title: "Product",
            dataIndex: "",
            align: "center",
            key: "product",
            render: (_, record) => {
              return (
                record?.products?.length > 0 && (
                  <Button
                    type="text"
                    icon={<PlayCircle size={20} style={{ color: defaultColors.primary }} />}
                    onClick={() => {
                      navigate("/product", {
                        state: {
                          operation_data: record,
                        },
                      });
                    }}
                  />
                )
              );
            },
          },
          {
            title: "Sampling",
            dataIndex: "",
            align: "center",
            key: "sampling",
            render: (_, record) => {
              return (
                record?.products?.length > 0 && (
                  <Button
                    type="text"
                    icon={<PlayCircle size={20} style={{ color: defaultColors.primary }} />}
                    onClick={() => {
                      navigate("/sampling", {
                        state: {
                          operation_data: record,
                        },
                      });
                    }}
                  />
                )
              );
            },
          },
        ]
      : [];
  };

  const allColumns = [
    {
      title: "Batch",
      dataIndex: "batch_number",
      key: "batch_number",
      defaultVisible: true,
      sortType: "string",
    },
  ];

  const processColumns = [
    { title: "Process", dataIndex: "process_name", key: "process_name" },
  ];

  const operationColumns = [
    {
      title: "Operation",
      dataIndex: "operation_number",
      key: "operation_number",
      render: (_, record) =>
        `${record?.operation_type}-${record?.operation_number}`,
    },
    {
      title: "Instruction",
      dataIndex: "instruction",
      key: "instruction",
    },
    {
      title: "Minimum Duration",
      dataIndex: "min_duration",
      key: "min_duration",
    },
    {
      title: "Maximum Duration",
      dataIndex: "max_duration",
      key: "max_duration",
    },
    ...get_add_routes(),
  ];

  const expandableRowRender = (record) => {
    return (
      <DataTable
        columns={operationColumns}
        data={record.operations}
        bordered
        height={null}
        pagination={false}
        rowKey={(record) => record.operation_id}
      />
    );
  };

  const expandableProcessRowRender = (record) => {
    return (
      <DataTable
        columns={processColumns}
        data={record.process}
        bordered
        height={null}
        expandable={{
          expandedRowRender: expandableRowRender,
          rowExpandable: (record) =>
            record.operations && record.operations.length > 0,
        }}
        pagination={false}
        rowKey={(record) => record.process_id}
      />
    );
  };

  return (
    <MainCard>
      <DataTable
        tableKey="batch_list"
        searchable
        searchPlaceholder="Search batches..."
        columns={allColumns}
        data={operation_data}
        bordered
        height={null}
        expandable={{
          expandedRowRender: expandableProcessRowRender,
          rowExpandable: (record) =>
            record.process && record.process.length > 0,
        }}
        rowKey={(record) => record.batch_id}
      />
    </MainCard>
  );
};

export default Batch;
